import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { CallTree } from '../components/CallTree';
import { OpcodeStepper } from '../components/OpcodeStepper';
import { StorageDiff } from '../components/StorageDiff';
import { TraceResponse, Network } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import { Terminal, AlertCircle, Settings, Share2, Download, Zap, Activity as ActivityIcon, Check } from 'lucide-react';
import { cn } from '../lib/utils';
import { useSettings } from '../contexts/SettingsContext';

export const TxDebug: React.FC = () => {
  const { network: routeNetwork, txHash: routeTxHash } = useParams<{ network: string; txHash: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const routeApiKey = searchParams.get('apiKey') || '';
  const routeProvider = searchParams.get('provider') || 'infura';

  const [txHash, setTxHash] = useState(routeTxHash || '');
  const [network, setNetwork] = useState<Network>((routeNetwork as Network) || 'ethereum');
  const [apiKey, setApiKey] = useState(routeApiKey);
  const [provider, setProvider] = useState(routeProvider);
  const [traceData, setTraceData] = useState<TraceResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [errorSuggestion, setErrorSuggestion] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [storageHeight, setStorageHeight] = useState(240);
  const [isResizing, setIsResizing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const handleExport = () => {
    if (!traceData) return;
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(traceData, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `oru-trace-${network}-${txHash}.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { settings } = useSettings();

  const startResizing = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      // Calculate new height from bottom of screen
      // window.innerHeight - mouse position - footer height (32px)
      const newHeight = window.innerHeight - e.clientY - 32;
      
      // Constraints
      if (newHeight > 120 && newHeight < window.innerHeight * 0.6) {
        setStorageHeight(newHeight);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };
  }, [isResizing]);

  const fetchTrace = async () => {
    if (!txHash) return;
    setIsLoading(true);
    setError(null);
    setErrorDetails(null);
    setErrorSuggestion(null);
    try {
      const q = new URLSearchParams();
      if (apiKey) q.set('apiKey', apiKey);
      if (provider) q.set('provider', provider);
      const queryString = q.toString() ? `?${q.toString()}` : '';

      const response = await fetch(`/api/trace/${network}/${txHash}${queryString}`);
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to fetch trace data');
        setErrorDetails(data.details || null);
        setErrorSuggestion(data.suggestion || null);
        throw new Error(data.error || 'Failed to fetch trace data');
      }
      const data = await response.json();
      setTraceData(data);
      setCurrentStepIndex(0);
    } catch (err: any) {
      if (!error) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (routeNetwork) {
      setNetwork(routeNetwork as Network);
    }
    if (routeTxHash) {
      setTxHash(routeTxHash);
      setApiKey(searchParams.get('apiKey') || '');
      setProvider(searchParams.get('provider') || 'infura');
      fetchTrace();
    }
  }, [routeTxHash, routeNetwork, location.search]);

  const handleSearch = () => {
    const q = new URLSearchParams();
    if (apiKey) q.set('apiKey', apiKey);
    if (provider) q.set('provider', provider);
    const queryString = q.toString() ? `?${q.toString()}` : '';

    if (txHash !== routeTxHash || network !== routeNetwork || apiKey !== routeApiKey || provider !== routeProvider) {
      navigate(`/app/tx/${network}/${txHash}${queryString}`);
    } else {
      fetchTrace();
    }
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-[#0A0A0A]">
      {/* Top Bar */}
      <Header 
        txHash={txHash}
        setTxHash={setTxHash}
        network={network}
        setNetwork={setNetwork}
        apiKey={apiKey}
        setApiKey={setApiKey}
        provider={provider}
        setProvider={setProvider}
        onSearch={handleSearch}
        isLoading={isLoading}
      />

      {/* Sub-Header / Status Bar */}
      <div className="min-h-10 border-b border-[#262626] bg-[#0F0F0F] flex flex-wrap items-center px-4 py-2 md:py-0 justify-between text-[10px] font-mono text-[#525252] gap-4">
        <div className="flex flex-wrap items-center gap-4 md:gap-6">
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-1.5 h-1.5 rounded-full",
              traceData?.isLiteMode ? "bg-yellow-500" : "bg-emerald-500"
            )} />
            <span className="text-white font-bold uppercase tracking-widest">
              {traceData?.isLiteMode ? "Lite Mode" : "Live Session"}
            </span>
          </div>
          <div className="hidden md:block h-3 w-[1px] bg-[#262626]" />
          <div className="flex items-center gap-2">
            <Zap className="w-3 h-3 text-yellow-500" />
            <span className="uppercase tracking-widest">Fork: Off</span>
          </div>
          <div className="hidden md:block h-3 w-[1px] bg-[#262626]" />
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-widest">
              Block: {traceData?.blockNumber ? Number(traceData.blockNumber).toLocaleString() : '...'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button 
            onClick={handleShare}
            className={cn(
              "flex items-center gap-1.5 transition-colors uppercase tracking-widest",
              copySuccess ? "text-emerald-500" : "hover:text-white"
            )}
          >
            {copySuccess ? <Check className="w-3 h-3" /> : <Share2 className="w-3 h-3" />}
            <span className="hidden sm:inline">{copySuccess ? 'Copied' : 'Share'}</span>
          </button>
          <button 
            onClick={handleExport}
            disabled={!traceData}
            className="flex items-center gap-1.5 hover:text-white transition-colors uppercase tracking-widest disabled:opacity-30"
          >
            <Download className="w-3 h-3" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button 
            onClick={() => navigate('/app/settings')}
            className="flex items-center gap-1.5 hover:text-white transition-colors uppercase tracking-widest"
          >
            <Settings className="w-3 h-3" />
            <span className="hidden sm:inline">Settings</span>
          </button>
        </div>
      </div>

      <main className="flex-1 flex flex-col md:flex-row overflow-hidden">
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col items-center justify-center gap-4 bg-[#0A0A0A]"
            >
              <div className="w-12 h-12 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
              <div className="font-mono text-xs text-[#525252] animate-pulse uppercase tracking-[0.3em]">
                PARSING EVM TRACE DATA...
              </div>
            </motion.div>
          ) : error ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex-1 flex flex-col items-center justify-center gap-4 bg-[#0A0A0A] p-6"
            >
              <AlertCircle className="w-12 h-12 text-red-500/50" />
              <div className="text-center max-w-lg">
                <h3 className="text-lg font-bold text-white uppercase tracking-tighter">Trace Error</h3>
                <p className="text-[#525252] font-mono text-sm mt-2">{error}</p>
                {errorDetails && (
                  <p className="text-[#404040] font-mono text-xs mt-1">{errorDetails}</p>
                )}
                {errorSuggestion && (
                  <div className="mt-4 p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                    <p className="text-[10px] text-emerald-400 font-mono uppercase tracking-widest leading-relaxed">
                      💡 Pro-Tip: {errorSuggestion}
                    </p>
                  </div>
                )}
                {!errorSuggestion && traceData === null && !isLoading && (
                  <div className="mt-4 p-4 bg-red-500/5 border border-red-500/20 rounded-lg">
                    <p className="text-[10px] text-red-400/80 font-mono uppercase tracking-widest leading-relaxed">
                      Technical Details: The RPC request failed after several fallback attempts. This usually means the public endpoints are rate-limited or the network is experiencing high latency.
                    </p>
                  </div>
                )}
              </div>
              <button 
                onClick={fetchTrace}
                className="px-6 py-3 bg-[#1A1A1A] border border-[#262626] rounded-lg text-[10px] font-bold uppercase tracking-widest hover:border-emerald-500/50 transition-all text-white"
              >
                RETRY FETCH
              </button>
            </motion.div>
          ) : traceData ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex-1 flex flex-col overflow-y-auto md:overflow-hidden"
            >
              {traceData.isLiteMode && (
                <div className="bg-yellow-500/5 border-b border-yellow-500/20 p-3 flex items-center gap-3">
                  <AlertCircle className="w-4 h-4 text-yellow-500" />
                  <div className="flex-1">
                    <p className="text-[10px] text-yellow-500/80 font-mono uppercase tracking-widest font-bold">
                      Lite Mode Active
                    </p>
                    <p className="text-[10px] text-[#525252] font-mono mt-0.5">
                      The current RPC endpoint does not support advanced tracing. Falling back to high-level details.
                    </p>
                  </div>
                </div>
              )}
              <div className="flex-1 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden">
                <div className="w-full md:w-1/3 h-[300px] md:h-full border-b md:border-b-0 md:border-r border-[#262626]">
                  <CallTree root={traceData.callTree} />
                </div>
                <div className="flex-1 h-[500px] md:h-full">
                  <OpcodeStepper 
                    steps={traceData.trace}
                    currentStepIndex={currentStepIndex}
                    onStepChange={setCurrentStepIndex}
                  />
                </div>
              </div>

              {/* Resize Handle */}
              <div 
                onMouseDown={startResizing}
                className="hidden md:block h-2 w-full cursor-row-resize relative z-20 group touch-none"
              >
                <div className={cn(
                  "absolute inset-x-0 top-1/2 -translate-y-1/2 h-px transition-all",
                  isResizing ? "bg-emerald-500 h-[2px]" : "bg-[#262626] group-hover:bg-emerald-500/50 group-hover:h-[2px]"
                )} />
              </div>

              <div 
                className={cn(
                  "border-t border-[#262626] shrink-0",
                  isResizing && "pointer-events-none"
                )}
                style={{ height: isMobile ? 'auto' : `${storageHeight}px` }}
              >
                <StorageDiff changes={traceData.storageChanges} />
              </div>
            </motion.div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-4 bg-[#0A0A0A]">
              <Terminal className="w-16 h-16 text-[#1A1A1A]" />
              <div className="text-center">
                <h2 className="text-xl font-bold tracking-tighter text-[#262626] uppercase">ORU TRACE ENGINE</h2>
                <p className="text-[#1A1A1A] font-mono text-xs uppercase tracking-widest mt-2">Ready for transaction input</p>
              </div>
            </div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer Status Bar */}
      <footer className="h-8 border-t border-[#262626] bg-[#0A0A0A] flex items-center px-4 justify-between text-[10px] font-mono text-[#525252]">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span>ENGINE: V1.0.4-STABLE</span>
          </div>
          <div className="h-3 w-[1px] bg-[#262626]" />
          <span>LATENCY: 42ms</span>
        </div>
        <div className="flex items-center gap-4">
          {settings.telemetry && (
            <div className="flex items-center gap-1.5 text-emerald-500/50">
              <ActivityIcon className="w-3 h-3 animate-pulse" />
              <span>TELEMETRY ACTIVE</span>
              <div className="h-3 w-[1px] bg-[#262626] mx-2" />
            </div>
          )}
          <span>{new Date().toISOString()}</span>
          <div className="h-3 w-[1px] bg-[#262626]" />
          <span className="text-emerald-500/70">SECURE SESSION</span>
        </div>
      </footer>
    </div>
  );
};
